/**
 * The player's progression and how a fight changes it (docs/game-design.md §2–5, §11).
 * Pure functions: pass the time and time zone in. Stored by the client (localStorage in M1)
 * and by the server from M4.
 */
import type { Look } from '../config/cosmetics';
import { ECONOMY } from '../config/economy';
import { PROGRESSION } from '../config/progression';
import { emptyRanks, MAX_RANK, type SkillId, type SkillRanks } from '../config/skills';
import { dayKey, previousDayKey, previousWeekKey, seasonKey, weekKey } from './calendar';
import { addSkillXp, levelFromXp, type LevelInfo } from './level';
import { rankCost, spentPoints } from './training';
import { calcFightXp, weeklyBandFactor, type Outcome, type XpResult } from './xp';

export interface Progress {
  version: 1;
  createdAt: number;
  xpTotal: number;
  skillPointsEarned: number;
  skillBarXp: number;
  bonusPoints: number;
  ranks: SkillRanks;
  coins: number;
  weekKey: string;
  weekFights: number;
  lastWeekFights: number;
  restedLeft: number;
  dayKey: string;
  wonToday: boolean;
  streakDays: number;
  streakLastDay: string | null;
  record: { wins: number; losses: number; draws: number; kos: number };
  /** Season in which the free skill reset was used. */
  freeResetSeason: number | null;
  /**
   * The Locker (M7, shared/src/progression/locker.ts): cosmetics bought, as "kind:id", and the
   * look worn. Missing in progress saved before the shop: read them through ownedItems / lookOf.
   */
  owned?: string[];
  look?: Look;
}

export function newProgress(nowMs: number, timeZone: string): Progress {
  return {
    version: 1,
    createdAt: nowMs,
    xpTotal: 0,
    skillPointsEarned: 0,
    skillBarXp: 0,
    bonusPoints: 0,
    ranks: emptyRanks(),
    coins: 0,
    weekKey: weekKey(nowMs),
    weekFights: 0,
    lastWeekFights: 0,
    // A new player had fewer than 20 fights last week, so they start rested.
    restedLeft: PROGRESSION.rested.fights,
    dayKey: dayKey(nowMs, timeZone),
    wonToday: false,
    streakDays: 0,
    streakLastDay: null,
    record: { wins: 0, losses: 0, draws: 0, kos: 0 },
    freeResetSeason: null,
  };
}

/** Moves week and day counters forward to `nowMs`. */
export function rollover(p: Progress, nowMs: number, timeZone: string): Progress {
  const next = { ...p };
  const wk = weekKey(nowMs);
  if (wk !== p.weekKey) {
    next.lastWeekFights = previousWeekKey(wk) === p.weekKey ? p.weekFights : 0;
    next.weekKey = wk;
    next.weekFights = 0;
    next.restedLeft = next.lastWeekFights < PROGRESSION.rested.lastWeekBelow ? PROGRESSION.rested.fights : 0;
  }
  const day = dayKey(nowMs, timeZone);
  if (day !== p.dayKey) {
    next.dayKey = day;
    next.wonToday = false;
  }
  return next;
}

export function availablePoints(p: Progress): number {
  return p.skillPointsEarned + p.bonusPoints - spentPoints(p.ranks);
}

export function levelOf(p: Progress): LevelInfo {
  return levelFromXp(p.xpTotal);
}

export interface FightRecord {
  outcome: Outcome;
  ko: boolean;
  vsBot: boolean;
  /** Hidden rating difference, opponent − player (0 against bots). */
  ratingDiff: number;
  league?: { playedLive: boolean } | null;
}

export interface FightRewards {
  xp: XpResult;
  coins: number;
  overflowCoins: number;
  levelBefore: LevelInfo;
  levelAfter: LevelInfo;
  skillBarBefore: { pointsEarned: number; barXp: number };
  skillBarAfter: { pointsEarned: number; barXp: number };
  skillPointsGained: number;
  /** Weekly band factor for this fight and for the next one (a change triggers the popup). */
  bandFactor: number;
  nextBandFactor: number;
  restedLeft: number;
  streakDays: number;
  streakCompleted: boolean;
  weekFights: number;
}

export function applyFight(
  prev: Progress,
  fight: FightRecord,
  nowMs: number,
  timeZone: string,
): { progress: Progress; rewards: FightRewards } {
  const p = rollover(prev, nowMs, timeZone);
  const isLeague = !!fight.league;
  const win = fight.outcome === 'win';
  // A league fight a game plan fought while the player was away counts for nothing daily (§2.1).
  const played = !fight.league || fight.league.playedLive;

  // Streak: days in a row with at least one fight.
  let streakDays = p.streakDays;
  let streakCompleted = false;
  if (played && p.streakLastDay !== p.dayKey) {
    streakDays = p.streakLastDay === previousDayKey(p.dayKey) ? streakDays + 1 : 1;
    if (streakDays >= PROGRESSION.xp.streakDays) {
      streakCompleted = true;
      streakDays = 0;
    }
  }

  const fightNumber = isLeague ? p.weekFights : p.weekFights + 1;
  const rested = !isLeague && p.restedLeft > 0;
  const xp = calcFightXp({
    outcome: fight.outcome,
    ko: fight.ko,
    ratingDiff: fight.ratingDiff,
    vsBot: fight.vsBot,
    fightNumberThisWeek: Math.max(1, fightNumber),
    rested,
    firstWinOfDay: played && win && !p.wonToday,
    streakReward: streakCompleted,
    league: fight.league ?? null,
  });

  const levelBefore = levelFromXp(p.xpTotal);
  const barBefore = { pointsEarned: p.skillPointsEarned, barXp: p.skillBarXp };
  const bar = addSkillXp(barBefore, xp.total);
  const C = ECONOMY.coins;
  const coins = win ? (fight.ko ? C.koWin : C.win) : fight.outcome === 'draw' ? C.draw : C.loss;

  const progress: Progress = {
    ...p,
    xpTotal: p.xpTotal + xp.total,
    skillPointsEarned: bar.pointsEarned,
    skillBarXp: bar.barXp,
    coins: p.coins + coins + bar.overflowCoins,
    weekFights: isLeague ? p.weekFights : p.weekFights + 1,
    restedLeft: rested ? p.restedLeft - 1 : p.restedLeft,
    wonToday: p.wonToday || (played && win),
    streakDays,
    streakLastDay: played ? p.dayKey : p.streakLastDay,
    record: {
      wins: p.record.wins + (win ? 1 : 0),
      losses: p.record.losses + (fight.outcome === 'loss' ? 1 : 0),
      draws: p.record.draws + (fight.outcome === 'draw' ? 1 : 0),
      kos: p.record.kos + (win && fight.ko ? 1 : 0),
    },
  };

  return {
    progress,
    rewards: {
      xp,
      coins,
      overflowCoins: bar.overflowCoins,
      levelBefore,
      levelAfter: levelFromXp(progress.xpTotal),
      skillBarBefore: barBefore,
      skillBarAfter: { pointsEarned: bar.pointsEarned, barXp: bar.barXp },
      skillPointsGained: bar.gained,
      bandFactor: xp.bandFactor,
      nextBandFactor: weeklyBandFactor(progress.weekFights + 1),
      restedLeft: progress.restedLeft,
      streakDays,
      streakCompleted,
      weekFights: progress.weekFights,
    },
  };
}

export type TrainingError = 'notEnoughPoints' | 'invalidRank';

/** Confirms pending training: `ranks` must only raise ranks and fit the available points. */
export function confirmTraining(p: Progress, ranks: SkillRanks): Progress | TrainingError {
  for (const id of Object.keys(ranks) as SkillId[]) {
    const r = ranks[id];
    if (!Number.isInteger(r) || r < p.ranks[id] || r > MAX_RANK) return 'invalidRank';
  }
  if (spentPoints(ranks) > p.skillPointsEarned + p.bonusPoints) return 'notEnoughPoints';
  return { ...p, ranks: { ...ranks } };
}

/** Cost of raising one skill by one rank, or null at max. */
export function nextRankCost(p: Progress, id: SkillId): number | null {
  return p.ranks[id] >= MAX_RANK ? null : rankCost(p.ranks[id] + 1);
}

export function resetCost(p: Progress, nowMs: number, timeZone = 'UTC'): number {
  return p.freeResetSeason === seasonKey(nowMs, timeZone) ? ECONOMY.skillResetCost : 0;
}

/** Refunds all spent points. Free once per league season, then costs coins. */
export function resetSkills(p: Progress, nowMs: number, timeZone = 'UTC'): Progress | 'notEnoughCoins' {
  const cost = resetCost(p, nowMs, timeZone);
  if (p.coins < cost) return 'notEnoughCoins';
  return {
    ...p,
    ranks: emptyRanks(),
    coins: p.coins - cost,
    freeResetSeason: cost === 0 ? seasonKey(nowMs, timeZone) : p.freeResetSeason,
  };
}
